import { Box, Divider, Flex } from '@chakra-ui/layout';
import React, { useState } from 'react';

import { utils as commonUtils } from '@finley-technologies/common';

import { GetBalanceSheetExposureDataQueryGQLType } from './getBalanceSheetExposureData.gql.generated';
import { useFeatureFlags } from '~/contexts/FeatureFlagProvider';
import { useUserAuthOrThrow } from '~/contexts/UserAuthProvider';
import { colors } from '~/packages/common-frontend/colors';
import { Icon } from '~/packages/common-frontend/components/icons';
import { Typography } from '~/packages/common-frontend/components/Typography';
import { useSimpleColorMode } from '~/packages/common-frontend/theme/utils';

// Fragments
const PendingTapeUpload = () => (
  <Typography inline variant="base-muted" TextProps={{ textAlign: 'end' }}>
    The data will be available after the next tape upload.
  </Typography>
);
type TotalsRowProps = { label: React.ReactNode; amount: React.ReactNode };
const TotalsRow = ({ label, amount }: TotalsRowProps) => {
  const mode = useSimpleColorMode();
  return (
    <Flex
      alignItems="center"
      background={mode(colors.aurora[50], colors.aurora[900])}
      gap="1rem"
      minHeight="3.5rem"
      padding="0 1rem"
      width="100%"
    >
      <Typography
        inline
        variant="base"
        fontWeight="medium"
        TextProps={{ flexGrow: 1 }}
      >
        {label}
      </Typography>
      <Typography inline variant="base" fontWeight="medium">
        {amount}
      </Typography>
      <Box flexShrink={0} w="2.5rem" h="2.5rem" />
    </Flex>
  );
};

type SectionHeaderRowProps = {
  label: React.ReactNode;
  amount: React.ReactNode;
  children: React.ReactNode;
};
const SectionHeaderRow = ({
  label,
  amount,
  children,
}: SectionHeaderRowProps) => {
  const mode = useSimpleColorMode();
  const [isExpanded, setIsExpanded] = useState(false);
  return (
    <>
      <Flex
        alignItems="center"
        background={mode(colors.gray[50], colors.gray[900])}
        gap="1rem"
        minHeight="3.5rem"
        padding="0 1rem"
        width="100%"
        onClick={() => setIsExpanded((prev) => !prev)}
        cursor={children ? 'pointer' : 'auto'}
      >
        <Typography
          inline
          variant="base"
          fontWeight="medium"
          TextProps={{ flexGrow: 1 }}
        >
          {label}
        </Typography>
        <Typography inline variant="base" fontWeight="medium">
          {amount}
        </Typography>
        {children ? (
          <Box m="0px 8px">
            <Icon
              icon={isExpanded ? 'circle-chevron-up' : 'circle-chevron-down'}
              size="large"
            />
          </Box>
        ) : (
          <Box flexShrink={0} w="2.5rem" h="2.5rem" />
        )}
      </Flex>
      {isExpanded ? children : null}
    </>
  );
};

type SectionRowProps = { label: React.ReactNode; amount: React.ReactNode };
const SectionRow = ({ label, amount }: SectionRowProps) => (
  <Flex
    alignItems="center"
    gap="1rem"
    minHeight="3.5rem"
    padding="0 1rem"
    width="100%"
  >
    <Typography inline variant="base-muted" TextProps={{ flexGrow: 1 }}>
      {label}
    </Typography>
    <Typography inline variant="base-muted">
      {amount}
    </Typography>
    <Box flexShrink={0} w="2.5rem" h="2.5rem" />
  </Flex>
);

const BalanceSheetExposureBase = ({
  balanceSheetImpactCalcs,
}: {
  balanceSheetImpactCalcs: GetBalanceSheetExposureDataQueryGQLType | null;
}) => {
  if (!balanceSheetImpactCalcs?.balanceSheetImpactCalculations) {
    return null;
  }

  return (
    <Flex flexDirection="column" gap="1.5rem">
      <Typography variant="section-title" trim TextProps={{ flexGrow: 1 }}>
        Balance Sheet
      </Typography>
      <Flex flexDirection="column" gap="0.5rem">
        <TotalsRow
          label="Total Exposure"
          amount={
            <span>
              {commonUtils.numberToCurrencyWithSymbol({
                number:
                  balanceSheetImpactCalcs.balanceSheetImpactCalculations
                    .totalBalanceSheetExposure,
              })}
            </span>
          }
        />
        {balanceSheetImpactCalcs.balanceSheetImpactCalculations.loanProductCalculations.map(
          (calculation) => (
            <SectionHeaderRow
              key={calculation.loanProductId}
              label={calculation.loanProductName}
              amount={
                calculation.balanceSheetExposure !== null ? (
                  <span>
                    {commonUtils.numberToCurrencyWithSymbol({
                      number: calculation.balanceSheetExposure,
                    })}
                  </span>
                ) : (
                  <PendingTapeUpload />
                )
              }
            >
              {calculation.balanceSheetExposure !== null &&
                calculation.eligibleExposureForCreditFacilities.length > 0 &&
                calculation.eligibleExposureForCreditFacilities.map(
                  (exposure, index) => (
                    <React.Fragment key={exposure.creditFacility.id}>
                      {index !== 0 && <Divider />}
                      <SectionRow
                        label={`Eligible for ${exposure.creditFacility.name}`}
                        amount={
                          exposure.eligibleExposure !== null ? (
                            <span>
                              {commonUtils.numberToCurrencyWithSymbol({
                                number: exposure.eligibleExposure,
                              })}
                            </span>
                          ) : (
                            <PendingTapeUpload />
                          )
                        }
                      />
                    </React.Fragment>
                  ),
                )}
            </SectionHeaderRow>
          ),
        )}
      </Flex>
    </Flex>
  );
};

// Exports
export const BalanceSheetExposure = ({
  balanceSheetImpactCalcs,
}: {
  balanceSheetImpactCalcs: GetBalanceSheetExposureDataQueryGQLType | null;
}) => {
  const { user } = useUserAuthOrThrow();
  const flags = useFeatureFlags();

  if (
    !flags.showBalanceSheetExposure ||
    user.userType !== 'BORROWER' ||
    user.organization.slug === 'imprint'
  ) {
    return null;
  }
  return (
    <BalanceSheetExposureBase
      balanceSheetImpactCalcs={balanceSheetImpactCalcs}
    />
  );
};
